import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { cn } from '../../lib/utils';
import { Keyboard, Search } from 'lucide-react';

const DEFAULT_SHORTCUTS = [
  { id: 'next-claim', label: 'Next claim', keys: ['N'], category: 'Navigation' },
  { id: 'prev-claim', label: 'Previous claim', keys: ['P'], category: 'Navigation' },
  { id: 'submit', label: 'Submit claim', keys: ['Ctrl', 'Enter'], category: 'Actions' },
  { id: 'submit-all', label: 'Submit all clean', keys: ['Ctrl', 'Shift', 'Enter'], category: 'Actions' },
  { id: 'edit-mode', label: 'Toggle edit mode', keys: ['E'], category: 'Editing' },
  { id: 'tab-next', label: 'Next field', keys: ['Tab'], category: 'Editing' },
  { id: 'tab-prev', label: 'Previous field', keys: ['Shift', 'Tab'], category: 'Editing' },
  { id: 'escape', label: 'Cancel edit / Close', keys: ['Esc'], category: 'Editing' },
  { id: 'save-field', label: 'Save field', keys: ['Enter'], category: 'Editing' },
  { id: 'scan', label: 'Scan with Lens', keys: ['Ctrl', 'L'], category: 'Actions' },
  { id: 'toggle-doc', label: 'Toggle document panel', keys: ['D'], category: 'Navigation' },
  { id: 'toggle-validation', label: 'Expand/Collapse validation', keys: ['V'], category: 'Navigation' },
  { id: 'shortcuts', label: 'Show shortcuts', keys: ['?'], category: 'General' },
  { id: 'return', label: 'Return to RevQ', keys: ['Ctrl', 'Shift', 'R'], category: 'General' },
  { id: 'assist-mode', label: 'Switch to Assist', keys: ['1'], category: 'Modes' },
  { id: 'autopilot-mode', label: 'Switch to Autopilot', keys: ['2'], category: 'Modes' },
  { id: 'accept-suggestion', label: 'Accept AI suggestion', keys: ['A'], category: 'Actions' },
  { id: 'chatbot', label: 'Open AI Chat', keys: ['C'], category: 'General' },
];

const KeyBadge = ({ children }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    minWidth: 22, height: 22, padding: '0 6px', borderRadius: 5,
    background: '#f5f5f5', border: '1px solid #d1d5db', boxShadow: '0 1px 0 #d1d5db',
    fontSize: 10, fontWeight: 500, color: '#555', fontFamily: 'system-ui',
  }}>{children}</span>
);

const KeyboardShortcutsDialog = ({ open, onOpenChange, customShortcuts, onUpdateShortcut }) => {
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [listeningKeys, setListeningKeys] = useState([]);
  const shortcuts = customShortcuts || DEFAULT_SHORTCUTS;

  const filtered = search
    ? shortcuts.filter(s => s.label.toLowerCase().includes(search.toLowerCase()) || s.category.toLowerCase().includes(search.toLowerCase()))
    : shortcuts;

  const categories = [...new Set(filtered.map(s => s.category))];

  const handleKeyCapture = useCallback((e) => {
    if (!editingId) return;
    e.preventDefault();
    const keys = [];
    if (e.ctrlKey || e.metaKey) keys.push('Ctrl');
    if (e.shiftKey) keys.push('Shift');
    if (e.altKey) keys.push('Alt');
    const key = e.key;
    if (!['Control', 'Shift', 'Alt', 'Meta'].includes(key)) {
      keys.push(key === ' ' ? 'Space' : key.length === 1 ? key.toUpperCase() : key);
    }
    if (keys.length > 0) {
      setListeningKeys(keys);
      if (onUpdateShortcut) onUpdateShortcut(editingId, keys);
      setTimeout(() => setEditingId(null), 300);
    }
  }, [editingId, onUpdateShortcut]);

  useEffect(() => {
    if (editingId) {
      window.addEventListener('keydown', handleKeyCapture);
      return () => window.removeEventListener('keydown', handleKeyCapture);
    }
  }, [editingId, handleKeyCapture]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden" data-testid="keyboard-shortcuts-dialog">
        <DialogHeader className="p-4 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Keyboard className="h-4 w-4" />
            Keyboard Shortcuts
          </DialogTitle>
        </DialogHeader>

        {/* Search */}
        <div className="px-4 py-2 border-b" style={{ background: '#fafafa' }}>
          <div className="flex items-center gap-2">
            <Search style={{ width: 14, height: 14, color: '#aaa' }} />
            <input
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search shortcuts..."
              className="flex-1 bg-transparent border-none outline-none text-sm"
              style={{ fontSize: 12 }}
              data-testid="shortcuts-search"
            />
          </div>
        </div>

        <div style={{ maxHeight: 420, overflowY: 'auto', padding: '8px 0' }}>
          {categories.map(cat => (
            <div key={cat}>
              <p style={{ fontSize: 10, fontWeight: 600, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '8px 16px 4px' }}>{cat}</p>
              {filtered.filter(s => s.category === cat).map(s => (
                <div
                  key={s.id}
                  className="flex items-center justify-between px-4 py-1.5 hover:bg-slate-50 transition-colors cursor-pointer"
                  onClick={() => { setEditingId(s.id); setListeningKeys([]); }}
                  data-testid={`shortcut-${s.id}`}
                >
                  <span style={{ fontSize: 12, color: '#333' }}>{s.label}</span>
                  <div className="flex items-center gap-1">
                    {editingId === s.id ? (
                      <span style={{ fontSize: 10, color: '#6b5ce7', fontStyle: 'italic', animation: 'pulse 1.5s infinite' }}>
                        {listeningKeys.length > 0 ? listeningKeys.join(' + ') : 'Press keys...'}
                      </span>
                    ) : (
                      s.keys.map((k, i) => (
                        <span key={i} className="flex items-center gap-0.5">
                          {i > 0 && <span style={{ fontSize: 9, color: '#ccc' }}>+</span>}
                          <KeyBadge>{k}</KeyBadge>
                        </span>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="p-3 border-t" style={{ background: '#fafafa' }}>
          <p style={{ fontSize: 10, color: '#aaa', textAlign: 'center' }}>Click any shortcut to customize · Press new key combination to reassign</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { DEFAULT_SHORTCUTS, KeyBadge };
export default KeyboardShortcutsDialog;
